import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useCartStore } from '../store';
import { Link } from 'react-router-dom';
import { ShoppingBag, AlertTriangle, ArrowRight, Trash2 } from 'lucide-react';

export default function CartPage() {
  const { foodCart, marketCart, removeItem, updateQuantity } = useCartStore();
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string>('');

  const { data: neighborhoods = [] } = useQuery({
    queryKey: ['neighborhoods'],
    queryFn: async () => {
      const { data } = await axios.get('http://localhost:5000/api/neighborhoods');
      return data;
    }
  });

  const selectedNeighborhood = neighborhoods.find((n: any) => n.id === selectedNeighborhoodId);

  // Her item'a hangi sepetten geldiğini belirten type tag'ı ekliyoruz
  const cartItems = [
    ...foodCart.map(i => ({ ...i, _type: 'food' as const })),
    ...marketCart.map(i => ({ ...i, _type: 'market' as const })),
  ];
  const cartTotal = cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  
  const minOrderAmount = selectedNeighborhood?.minOrderAmount || 0;
  const isBelowMin = selectedNeighborhood && cartTotal < minOrderAmount;
  const deliveryFee = selectedNeighborhood?.deliveryFee || 0;
  const finalTotal = cartTotal > 0 ? cartTotal + deliveryFee : 0;

  // Auto-select first active neighborhood
  useEffect(() => {
    if (!selectedNeighborhoodId && neighborhoods.length > 0) {
      const firstActive = neighborhoods.find((n: any) => n.isActive);
      if (firstActive) setSelectedNeighborhoodId(firstActive.id);
    }
  }, [neighborhoods, selectedNeighborhoodId]);

  return (
    <div className="container py-8 animate-fade-in">
      <h1 className="text-3xl font-bold flex items-center gap-3 mb-8">
        <ShoppingBag className="text-primary" size={32} />
        Sepetim
      </h1>

      <div className="grid md:grid-cols-3 gap-8">
        
        {/* Sol: Ürünler */}
        <div className="md:col-span-2 space-y-4">
          {cartItems.length === 0 ? (
            <div className="card text-center py-16">
              <ShoppingBag size={48} className="mx-auto text-muted mb-4 opacity-50" />
              <h3 className="text-xl font-bold text-muted mb-4">Sepetiniz Boş</h3>
              <Link to="/food" className="btn btn-primary">Yemek Söyle</Link>
            </div>
          ) : (
            cartItems.map(item => (
              <div key={item.productId} className="card p-4 flex gap-4 items-center">
                <div style={{ width: '80px', height: '80px', flexShrink: 0, overflow: 'hidden', borderRadius: '10px', backgroundColor: '#f5f5f5' }}>
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem' }}>🍔</div>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-bold">{item.name}</h4>
                  <p className="text-primary font-bold">{item.price} ₺</p>
                </div>
                <div className="flex items-center gap-3">
                  <button 
                    className="w-8 h-8 rounded-full bg-surface-2 flex items-center justify-center font-bold text-lg hover:bg-border"
                    onClick={() => updateQuantity(item._type, item.productId, item.quantity - 1)}
                  >-</button>
                  <span className="font-bold w-4 text-center">{item.quantity}</span>
                  <button 
                    className="w-8 h-8 rounded-full bg-surface-2 flex items-center justify-center font-bold text-lg hover:bg-border"
                    onClick={() => updateQuantity(item._type, item.productId, item.quantity + 1)}
                  >+</button>
                </div>
                <button 
                  onClick={() => removeItem(item._type, item.productId)}
                  className="btn btn-sm btn-ghost text-error ml-4"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Sağ: Sepet Özeti */}
        <div className="md:col-span-1">
          <div className="card sticky top-24 border border-primary/20 shadow-lg">
            <h3 className="font-bold text-lg mb-6">Sipariş Özeti</h3>
            
            <div className="mb-6">
              <label className="form-label font-bold text-primary">Teslimat Mahallesi</label>
              <select 
                className="form-input bg-surface-2" 
                value={selectedNeighborhoodId}
                onChange={e => setSelectedNeighborhoodId(e.target.value)}
              >
                <option value="" disabled>Mahalle Seçin</option>
                {neighborhoods.map((n: any) => (
                  <option key={n.id} value={n.id} disabled={!n.isActive}>
                    {n.name} {!n.isActive && '(Hizmet Dışı)'}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-3 mb-6 pb-6 border-b border-border">
              <div className="flex justify-between text-muted">
                <span>Ara Toplam</span>
                <span>{cartTotal.toFixed(2)} ₺</span>
              </div>
              <div className="flex justify-between text-muted">
                <span>Gönderim Ücreti</span>
                <span>{deliveryFee.toFixed(2)} ₺</span>
              </div>
              {selectedNeighborhood && (
                <div className="flex justify-between text-xs font-medium text-primary">
                  <span>Min. Sipariş Tutarı</span>
                  <span>{minOrderAmount.toFixed(2)} ₺</span>
                </div>
              )}
            </div>

            <div className="flex justify-between font-black text-xl mb-6">
              <span>Toplam</span>
              <span>{finalTotal.toFixed(2)} ₺</span>
            </div>

            {isBelowMin ? (
              <div className="bg-error/10 text-error p-3 rounded-lg flex gap-3 items-start mb-4 text-sm font-medium">
                <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                <p>
                  {selectedNeighborhood.name} için minimum sipariş tutarı <strong>{minOrderAmount} ₺</strong>'dir. Sepetinize <strong>{(minOrderAmount - cartTotal).toFixed(2)} ₺</strong>'lik daha ürün eklemelisiniz.
                </p>
              </div>
            ) : null}

            <Link 
              to="/checkout" 
              className={`btn btn-primary w-full ${(!selectedNeighborhood || isBelowMin || cartItems.length === 0) ? 'opacity-50 pointer-events-none' : ''}`}
            >
              Sepeti Onayla <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
